export interface Volume {
  number: string
  title: string
  subtitle: string
  intro: string[]
  questions?: string[]
  highlight?: string
  afterHighlight?: string
  bulletsIntro: string
  bullets: string[]
  sequenceIntro?: string
  sequence?: string
  secondaryBulletsIntro?: string
  secondaryBullets?: string[]
  resultLead?: string
  resultQuote?: string
  result: string
  resultEmphasis?: string
  popular?: boolean
}

export const volumes: Volume[] = [
  {
    number: 'Volume 01',
    title: 'Scuba Foundations, Equipment & First Lessons',
    subtitle: 'Build the foundation every new diver needs.',
    intro: [
      'A beginner needs more than information about scuba equipment.',
      'They need a clear teaching sequence that takes them from initial orientation toward safer, more controlled underwater participation.',
    ],
    bulletsIntro: 'This volume helps you teach:',
    bullets: [
      'Equipment familiarization',
      'Scuba setup concepts',
      'Pre-dive awareness',
      'Basic hand signals',
      'Buddy awareness',
      'First breathing experiences',
      'Body positioning',
      'Initial water orientation',
      'Equipment checks',
      'Beginner errors',
      'Instructor cues',
      'First-skill progressions',
      'Structured first lessons',
    ],
    result:
      'Your student develops stronger foundations while you follow a clear and repeatable teaching progression.',
  },
  {
    number: 'Volume 02',
    title: 'Buoyancy, Breathing, Trim & Underwater Control',
    subtitle:
      'Teach one of the most important foundations of confident scuba diving visually.',
    intro: [
      'Instead of repeatedly telling students to “fix their buoyancy,” you can show them exactly what is happening.',
    ],
    bulletsIntro: 'Inside:',
    bullets: [
      'Positive, neutral and negative buoyancy',
      'Breathing awareness',
      'BCD control',
      'Buoyancy delay',
      'Depth stability',
      'Weighting awareness',
      'Horizontal trim',
      'Equipment balance',
      'Leg position',
      'Hand control',
      'Hovering',
      'Start-and-stop control',
      'Streamlined positioning',
      'Environmental contact prevention',
      'Multitasking while maintaining buoyancy',
      'Common errors',
      'Instructor corrections',
    ],
    result:
      'Students can see the difference between unstable movement and controlled underwater positioning before trying to reproduce it.',
  },
  {
    number: 'Volume 03',
    title: 'Descents, Ascents, Equalization & Controlled Water Skills',
    subtitle: 'Turn vertical movement into a structured teaching progression.',
    intro: [
      'Teach students how to remain calm, oriented and controlled while changing depth.',
    ],
    bulletsIntro: 'Includes:',
    bullets: [
      'Descent preparation',
      'Equalization awareness',
      'Communication',
      'Student positioning',
      'Instructor positioning',
      'Controlled descent concepts',
      'Depth awareness',
      'Ascent preparation',
      'Controlled ascent concepts',
      'Surface approach',
      'Buddy coordination',
      'Common problems',
      'Correction cues',
      'Progressive skill development',
    ],
    result:
      'Vertical movement becomes something students can understand visually instead of trying to learn only through verbal explanation.',
  },
  {
    number: 'Volume 04',
    title: 'Mask, Regulator, Finning, Navigation & Buddy Skills',
    subtitle: 'Teach the core practical skills students need underwater.',
    intro: [],
    bulletsIntro: 'Inside:',
    bullets: [
      'Mask skills',
      'Regulator skills',
      'Regulator recovery concepts',
      'Controlled equipment handling',
      'Finning',
      'Propulsion',
      'Turns',
      'Body position during movement',
      'Underwater orientation',
      'Navigation fundamentals',
      'Buddy positioning',
      'Hand communication',
      'Task control',
      'Common errors',
      'Instructor cues',
      'Skill progressions',
    ],
    result:
      'Students build stronger practical skills while maintaining better underwater control and awareness.',
  },
  {
    number: 'Volume 05',
    title: 'Safety, Problem Management & Underwater Decision-Making',
    subtitle: 'Help students recognize problems before they become bigger problems.',
    intro: [],
    bulletsIntro: 'Includes visual teaching frameworks for:',
    bullets: [
      'Anxiety recognition',
      'Task overload',
      'Loss of orientation',
      'Buoyancy problems',
      'Buddy separation awareness',
      'Low visibility',
      'Current awareness',
      'Fatigue',
      'Equipment-problem recognition',
      'Communication breakdown',
      'Situational awareness',
      'Controlled decision-making',
      'Knowing when to stop and reset',
    ],
    result:
      "Students learn that good scuba diving is not only performing skills — it's recognizing changing situations and responding with better judgment.",
  },
  {
    number: 'Volume 06',
    title: '150 Ready-to-Teach Scuba Lessons — Beginner to Advanced',
    subtitle: 'Stop building every scuba lesson from scratch.',
    intro: ['This is the practical heart of the complete system.', 'You receive:'],
    highlight: '150 COMPLETE READY-TO-TEACH SCUBA LESSONS',
    afterHighlight: 'Organized so you can open the system, choose the objective and begin teaching.',
    bulletsIntro: 'Lesson areas include:',
    bullets: [
      'Foundations',
      'Equipment',
      'Buoyancy',
      'Breathing',
      'Trim',
      'Hovering',
      'Mask skills',
      'Regulator skills',
      'Finning',
      'Descents',
      'Ascents',
      'Navigation',
      'Buddy skills',
      'Underwater communication',
      'Safety',
      'Problem recognition',
      'Decision-making',
      'Skill integration',
      'Assessments',
      'Progressive student development',
    ],
    secondaryBulletsIntro: 'Lessons can include:',
    secondaryBullets: [
      'Objective',
      'Student level',
      'Setup',
      'Visual explanation',
      'Instructor demonstration',
      'Student practice',
      'What to observe',
      'Instructor cues',
      'Common errors',
      'Correction',
      'Easier version',
      'Progression',
      'Success check',
    ],
    resultLead: 'Instead of asking:',
    resultQuote: '“What am I going to teach this student today?”',
    result: 'You open the system and',
    resultEmphasis: 'YOU ALREADY HAVE THE LESSON.',
    popular: true,
  },
]
